import { Link, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { Card, CardContent } from '../../../components/ui/card'
import { TicketIcon, PlusIcon } from 'lucide-react'

interface AccessCode {
  id: number
  code: string
  name: string
  plan_tier: string
  duration_hours: number
  max_redemptions: number
  redemptions_count: number
  remaining_uses: number
  bonus_credits: number
  expires_at: string | null
  deactivated_at: string | null
  status: string
  created_by_email: string
  created_at: string
}

interface PagyMetadata {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Stats {
  total: number
  active: number
  total_redemptions: number
  active_redemptions: number
}

interface Props {
  access_codes: AccessCode[]
  pagy: PagyMetadata
  stats: Stats
  filters: { status: string }
  auth: { user: { id: number; name: string; email: string; admin: boolean; super_admin?: boolean } }
}

const STATUS_FILTERS = ['all', 'active', 'deactivated', 'expired', 'fully_redeemed'] as const

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active': return 'default'
    case 'deactivated': return 'destructive'
    case 'expired': return 'secondary'
    case 'fully_redeemed': return 'outline'
    default: return 'secondary'
  }
}

function formatDuration(hours: number): string {
  if (hours < 24) return `${hours}h`
  if (hours < 168) return `${Math.round(hours / 24)}d`
  return `${Math.round(hours / 168)}w`
}

function Index({ access_codes, pagy, stats, filters }: Props) {
  const { t } = useTranslation('admin')

  const handleFilterChange = (status: string) => {
    router.get('/admin/access_codes', { status }, { preserveState: true })
  }

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-2 py-2">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{t('accessCodes.index.title')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('accessCodes.index.description')}
                  </p>
                </div>
                <Link href="/admin/access_codes/new">
                  <Button>
                    <PlusIcon className="mr-2 h-4 w-4" />
                    {t('accessCodes.index.createCode')}
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{t('accessCodes.index.stats.total')}</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{t('accessCodes.index.stats.active')}</p>
                    <p className="text-2xl font-bold">{stats.active}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{t('accessCodes.index.stats.totalRedemptions')}</p>
                    <p className="text-2xl font-bold">{stats.total_redemptions}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{t('accessCodes.index.stats.activeRedemptions')}</p>
                    <p className="text-2xl font-bold">{stats.active_redemptions}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Filter Tabs */}
              <div className="mb-4 flex gap-2 flex-wrap">
                {STATUS_FILTERS.map((status) => (
                  <Button
                    key={status}
                    variant={filters.status === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleFilterChange(status)}
                  >
                    {t(`accessCodes.index.filters.${status === 'fully_redeemed' ? 'fullyRedeemed' : status}`)}
                  </Button>
                ))}
              </div>

              {/* Table */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.index.table.code')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.index.table.name')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.index.table.plan')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.index.table.duration')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.index.table.uses')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.index.table.status')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.index.table.created')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {access_codes.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                            <TicketIcon className="mx-auto mb-2 h-8 w-8 opacity-50" />
                            {t('accessCodes.index.empty')}
                          </td>
                        </tr>
                      ) : (
                        access_codes.map((code) => (
                          <tr key={code.id} className="border-b hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <Link href={`/admin/access_codes/${code.id}`} className="font-mono text-sm font-medium text-primary hover:underline">
                                {code.code}
                              </Link>
                            </td>
                            <td className="px-4 py-3 text-sm">{code.name}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="capitalize">{code.plan_tier}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">{formatDuration(code.duration_hours)}</td>
                            <td className="px-4 py-3 text-sm">{code.redemptions_count} / {code.max_redemptions}</td>
                            <td className="px-4 py-3">
                              <Badge variant={statusBadgeVariant(code.status)}>
                                {t(`accessCodes.badges.${code.status === 'fully_redeemed' ? 'fullyRedeemed' : code.status}`)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {new Date(code.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {pagy.from}–{pagy.to} of {pagy.count}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pagy.prev}
                      onClick={() => router.get('/admin/access_codes', { page: pagy.prev, status: filters.status }, { preserveState: true })}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pagy.next}
                      onClick={() => router.get('/admin/access_codes', { page: pagy.next, status: filters.status }, { preserveState: true })}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
